import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SumsubWebSdk from "@sumsub/websdk-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, FileSearch, BadgeCheck, CheckCircle2, Clock, ArrowRight,
  AlertCircle, Sparkles, Lock, Upload,
} from "lucide-react";

// Member-facing wording for an evidence slot's state. `pending` is deliberately
// "Submitted" rather than "Pending": from the member's side the file is in,
// and the word that matters is that compliance has it.
const ACC_SLOT_LABEL = {
  approved: "Accepted",
  pending:  "Submitted",
  rejected: "Needs attention",
  missing:  "Not provided",
};

const ACC_SLOT_BADGE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200 border",
  pending:  "bg-blue-50 text-blue-700 border-blue-200 border",
  rejected: "bg-rose-50 text-rose-700 border-rose-200 border",
  missing:  "bg-slate-50 text-slate-600 border-slate-200 border",
};

/**
 * Step 2 of the EchoSense canonical journey — INSIDE the logged-in portal.
 * Drives KYC (SumSub) and Accredited / Qualified Investor verification.
 * On both-approved, Step 3 milestone fires automatically (see backend
 * /api/onboarding/admin/mark-* endpoints + /api/kyc/{id}/review trigger).
 */
const KYCVerificationPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingKyc, setStartingKyc] = useState(false);
  const [startingAcc, setStartingAcc] = useState(false);
  const [sdkToken, setSdkToken] = useState(null);
  const [showWidget, setShowWidget] = useState(false);
  // STARSEED-CAPMARKETS-178 — the member's accreditation evidence slots. Read
  // from `/compliance/me`, which serves the same rows the reviewer acts on, so
  // the member and the operator cannot see different states for the same file.
  const [accItems, setAccItems] = useState([]);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  // FastAPI `detail` is a string on most endpoints and an object on the gate
  // ones (`{message, missing}`). Reading it raw puts an object into
  // `toast.error`, which renders "[object Object]" and loses the message — on
  // precisely the refusals that have something useful to say (CAPMARKETS-179).
  const errMsg = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (detail?.message) return detail.message;
    return e?.message || fallback;
  };

  const loadAccreditation = async () => {
    try {
      const r = await api.get("/compliance/me");
      setAccItems(r.data?.accreditation_items || []);
      return;
    } catch {
      /* fall through to the catalog below */
    }
    // The STATE read failed. Fall back to the catalog so the member still has
    // an uploader (/code-review on #2440): without this the slots vanished
    // while the Submit button stayed disabled telling them to "upload at least
    // one document below" — pointing at nothing, with no error shown because
    // the failure was swallowed. That dead-end instruction is the shape
    // CAPMARKETS-179 was filed for.
    //
    // Every slot defaults to `missing`, which is the safe direction: the member
    // can always upload, and the server refuses a submission that has no
    // evidence behind it regardless of what this page believes.
    try {
      const c = await api.get("/accreditation/catalog");
      setAccItems((c.data?.documents || []).map((d) => ({
        ...d, state: "missing", doc_id: null, file_name: null,
      })));
    } catch {
      /* Both reads failed — the section degrades to today's button. */
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/onboarding/status");
      setData(r.data);
      await loadAccreditation();
    } catch (e) {
      toast.error("Could not load onboarding status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/onboarding/status");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error("Could not load onboarding status");
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Routed through `loadAccreditation` rather than repeating the read
      // inline, so the mount path gets the catalog fallback too. The inline
      // copy also reported a `/compliance/me` failure as "Could not load
      // onboarding status", which named the wrong endpoint.
      if (!cancelled) await loadAccreditation();
    })();
    return () => { cancelled = true; };
  }, []);

  const startKyc = async () => {
    setStartingKyc(true);
    try {
      const r = await api.post("/onboarding/sumsub/start", {});
      if (r.data.configured && r.data.sdk_token) {
        // Live SumSub — open the verification widget inline.
        setSdkToken(r.data.sdk_token);
        setShowWidget(true);
      } else {
        toast.success("KYC step queued — SumSub credentials pending");
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start KYC");
    } finally {
      setStartingKyc(false);
    }
  };

  // SumSub WebSDK calls this when the short-lived token expires — re-mint one.
  const refreshSdkToken = async () => {
    try {
      const r = await api.post("/onboarding/sumsub/start", {});
      if (r.data.sdk_token) {
        setSdkToken(r.data.sdk_token);
        return r.data.sdk_token;
      }
    } catch (e) {
      console.error("Could not refresh SumSub token:", e);
    }
    return sdkToken;
  };

  const closeWidget = () => {
    setShowWidget(false);
    load();
  };

  const onSdkMessage = (type) => {
    // Refresh our portal status when the applicant submits / status changes.
    if (
      typeof type === "string" &&
      (type.includes("StatusChanged") ||
        type.includes("applicantSubmitted") ||
        type.includes("Completed"))
    ) {
      load();
    }
  };

  // STARSEED-CAPMARKETS-178 AC2. The upload goes through `/kyc/upload`, the
  // same endpoint and the same `kyc_documents` collection the KYC slots use —
  // which is what makes the reviewer's view-gate, the four-eyes rule and the
  // document access log apply to accreditation evidence without any of them
  // being re-implemented (AC8).
  const uploadEvidence = async (slot, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setUploadingSlot(slot);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/kyc/upload?document_type=${slot}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      await loadAccreditation();
    } catch (e) {
      toast.error(errMsg(e, "Failed to upload document"));
    } finally {
      setUploadingSlot(null);
    }
  };

  const startAccreditation = async () => {
    setStartingAcc(true);
    try {
      const r = await api.post("/onboarding/accreditation/start", {});
      toast.success(r.data.configured
        ? "Accredited verification initiated"
        : "Accredited review submitted");
      load();
    } catch (e) {
      // The endpoint now refuses a submission with no evidence (AC3), and that
      // refusal names the slots — so it has to reach the member as words.
      toast.error(errMsg(e, "Could not submit accredited review"));
    } finally {
      setStartingAcc(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl space-y-4" data-testid="kyc-verify-loading">
          <div className="h-10 w-72 bg-slate-100 rounded animate-pulse" />
          <div className="h-48 bg-slate-100 rounded-lg animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  const kycApproved        = data?.kyc_status === "approved";
  const kycInReview        = data?.kyc_status === "in_review";
  const accApproved        = data?.accredited_status === "approved" || data?.accredited_status === "verified";
  const accInReview        = data?.accredited_status === "in_review";
  const milestoneAchieved  = !!data?.compliance_milestone;
  // At least one slot filled. The server is the control (AC3 refuses a
  // submission without it); this only stops the member pressing a button that
  // is going to be refused.
  const hasAccEvidence     = accItems.some((i) => i.state !== "missing");

  return (
    <DashboardLayout>
      {/* SumSub WebSDK verification overlay */}
      {showWidget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="sumsub-widget-overlay"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-900">
                Identity Verification — SumSub
              </p>
              <button
                onClick={closeWidget}
                className="text-slate-500 hover:text-slate-900 text-sm font-medium"
                data-testid="sumsub-widget-close"
              >
                Close
              </button>
            </div>
            <div className="overflow-y-auto p-2" style={{ minHeight: 480 }}>
              {sdkToken && (
                <SumsubWebSdk
                  accessToken={sdkToken}
                  expirationHandler={refreshSdkToken}
                  config={{ lang: "en" }}
                  options={{ addViewportTag: false, adaptIframeHeight: true }}
                  onMessage={onSdkMessage}
                  onError={(e) => console.error("SumSub WebSDK error:", e)}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="max-w-4xl space-y-6" data-testid="kyc-verification-page">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={14} className="text-blue-600" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-blue-700 font-semibold">
              EchoSense Capital Club · Step 2 of Onboarding
            </p>
          </div>
          <h1 className="text-3xl font-serif text-slate-900" data-testid="kyc-page-title">
            KYC &amp; Accredited Investor Verification
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
            Complete identity verification and accredited / qualified investor review to advance
            your EchoSense profile to <strong>Accredited / KYC Approved</strong>. After this step,
            your file becomes eligible for the approved regulated partner onboarding pathway.
          </p>
        </div>

        {/* Progress card */}
        <Card className="bg-white border-slate-200" data-testid="kyc-progress-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Member Reference</p>
                <span className="font-mono text-sm text-slate-900" data-testid="kyc-vc-number">{data.vc_number || "—"}</span>
              </div>
              {milestoneAchieved ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="kyc-milestone-badge">
                  <Sparkles size={12} className="mr-1" /> Accredited / KYC Approved
                </Badge>
              ) : (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid="kyc-status-pending">
                  <Clock size={12} className="mr-1" /> In Progress · {data.completion_pct}%
                </Badge>
              )}
            </div>

            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
                style={{ width: `${data.completion_pct}%` }}
                data-testid="kyc-progress-bar"
              />
            </div>
          </CardContent>
        </Card>

        {/* Step 2a — KYC / AML */}
        <Card className="bg-white border-slate-200" data-testid="kyc-section">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                  <FileSearch size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Section 1</p>
                  <h2 className="text-lg font-semibold text-slate-900">KYC / AML Verification</h2>
                  <p className="text-sm text-slate-600 mt-1 max-w-md leading-relaxed">
                    Identity, residency, and sanctions screening via SumSub. Completion typically takes
                    5-10 minutes.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {kycApproved ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="kyc-status-approved">
                    <CheckCircle2 size={12} className="mr-1" /> Approved
                  </Badge>
                ) : kycInReview ? (
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 border" data-testid="kyc-status-in-review">
                    <Clock size={12} className="mr-1" /> In Review
                  </Badge>
                ) : (
                  <Badge className="bg-slate-50 text-slate-600 border-slate-200 border" data-testid="kyc-status-not-started">
                    Not started
                  </Badge>
                )}
                {!kycApproved && (
                  data.sumsub_configured ? (
                    <Button
                      onClick={startKyc}
                      disabled={startingKyc || kycInReview}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="kyc-start-btn"
                    >
                      {startingKyc ? "Starting…" : (
                        <>
                          {kycInReview ? "Continue KYC" : "Start KYC with SumSub"}
                          <ArrowRight size={12} className="ml-1.5" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate("/kyc")}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="kyc-upload-btn"
                    >
                      <Upload size={12} className="mr-1.5" />
                      {kycInReview ? "Manage Documents" : "Upload Documents"}
                      <ArrowRight size={12} className="ml-1.5" />
                    </Button>
                  )
                )}
              </div>
            </div>
            {!data.sumsub_configured && !kycApproved && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Automated identity verification is not enabled yet. Use the manual document review
                  instead:{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/kyc")}
                    className="font-semibold underline hover:text-amber-900"
                    data-testid="kyc-manual-link"
                  >
                    upload your government ID, proof of address, and a selfie
                  </button>{" "}
                  (also available under <span className="font-semibold">Documents</span> in the sidebar).
                  An admin reviews each file and approves your KYC.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2b — Accredited / Qualified */}
        <Card className="bg-white border-slate-200" data-testid="accredited-section">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                  kycApproved ? "bg-blue-50 border border-blue-100" : "bg-slate-50 border border-slate-200"
                }`}>
                  {kycApproved
                    ? <BadgeCheck size={18} className="text-blue-600" />
                    : <Lock size={18} className="text-slate-400" />}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Section 2</p>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Accredited / Qualified Investor Review
                  </h2>
                  <p className="text-sm text-slate-600 mt-1 max-w-md leading-relaxed">
                    Eligibility under U.S. Reg D / Reg S or a non-U.S. equivalent. Unlocks after KYC
                    approval.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {accApproved ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="accredited-status-approved">
                    <CheckCircle2 size={12} className="mr-1" /> Approved
                  </Badge>
                ) : accInReview ? (
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 border" data-testid="accredited-status-in-review">
                    <Clock size={12} className="mr-1" /> In Review
                  </Badge>
                ) : (
                  <Badge className="bg-slate-50 text-slate-600 border-slate-200 border" data-testid="accredited-status-not-started">
                    {kycApproved ? "Ready to start" : "Locked — complete KYC first"}
                  </Badge>
                )}
                {!accApproved && (
                  <Button
                    onClick={startAccreditation}
                    disabled={!kycApproved || startingAcc || accInReview || !hasAccEvidence}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                    title={hasAccEvidence
                      ? "Submit your evidence for review"
                      : "Upload at least one document below before submitting"}
                    data-testid="accredited-start-btn"
                  >
                    {startingAcc ? "Submitting…" : (
                      <>
                        {accInReview ? "Continue Review" : "Submit for Review"}
                        <ArrowRight size={12} className="ml-1.5" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* CAPMARKETS-178 AC2 — the three D2 evidence slots. Before this
                the button posted an empty body, so "in review" meant only that
                the member had pressed it: there was no accreditation document
                type in the catalog, and the reviewer opened the file to find
                nothing to read. No slot is individually required — the
                reviewer judges sufficiency — but at least one is needed to
                submit, which is what makes `in_review` a claim about a
                submission rather than about a click (AC3). */}
            {kycApproved && !accApproved && accItems.length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-200" data-testid="accreditation-slots">
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                  Supporting evidence
                </p>
                <p className="text-xs text-slate-600 mt-1 mb-3 max-w-lg leading-relaxed">
                  Provide whichever of these applies to you — a third-party letter may stand on
                  its own, and a statement alongside a signed self-certification may equally
                  suffice. Compliance reviews each document individually.
                </p>
                <ul className="space-y-2">
                  {accItems.map((item) => (
                    <li
                      key={item.type}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200"
                      data-testid={`accreditation-slot-${item.type}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">{item.label}</p>
                        <p className="text-[11px] text-slate-500 truncate">
                          {item.file_name || item.hint}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={ACC_SLOT_BADGE[item.state] || ACC_SLOT_BADGE.missing}>
                          {ACC_SLOT_LABEL[item.state] || item.state}
                        </Badge>
                        {/* The input is visually hidden but NOT `display:none`
                            and NOT removed from the tab order — a
                            `hidden` input inside a plain <label> is
                            unreachable by keyboard, which made every upload
                            control here mouse-only (Copilot on #2440). The
                            label carries the focus ring on behalf of the input
                            it wraps. */}
                        <label
                          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border
                                     border-slate-200 text-xs text-slate-700 hover:bg-slate-50
                                     cursor-pointer focus-within:ring-2 focus-within:ring-blue-500
                                     focus-within:ring-offset-1"
                        >
                          <Upload size={12} />
                          {uploadingSlot === item.type
                            ? "Uploading…"
                            : item.state === "missing" ? "Upload" : "Replace"}
                          <input
                            type="file"
                            className="sr-only"
                            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                            disabled={uploadingSlot === item.type}
                            onChange={(e) => {
                              uploadEvidence(item.type, e.target.files?.[0]);
                              e.target.value = "";   // re-selecting the same file must re-fire
                            }}
                            data-testid={`accreditation-upload-${item.type}`}
                          />
                        </label>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 3 — Milestone */}
        {milestoneAchieved && (
          <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200" data-testid="kyc-milestone-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-emerald-700" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Step 3 · Compliance Milestone</p>
                  <h2 className="text-lg font-semibold text-slate-900 mt-1">You are Accredited / KYC Approved</h2>
                  <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                    Your EchoSense Capital Club profile has been upgraded. Confirmation emails were sent
                    to you, your introducer, and the EchoSense compliance inbox.
                    <span className="block mt-2 text-slate-600 text-xs">
                      Next step: you&apos;ll be contacted by the approved regulated onboarding partner directly.
                      Do not send funds to EchoSense, any introducer, or any unapproved account. Only the
                      regulated partner / fund administrator issues written funding instructions.
                    </span>
                  </p>
                  {data.compliance_milestone_at && (
                    <p className="text-[11px] text-slate-500 mt-3 font-mono" data-testid="kyc-milestone-at">
                      Recorded {new Date(data.compliance_milestone_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compliance footer */}
        <Separator className="bg-slate-200" />
        <p className="text-[11px] text-slate-500 leading-relaxed max-w-2xl" data-testid="kyc-page-compliance-notice">
          EchoSense Capital Club provides a private member, introducer, and verification portal.
          EchoSense does not receive client funds, provide investment advice, guarantee returns, manage
          trading accounts, issue tax forms, or approve investor participation. Final account approval,
          funding instructions, tax reporting, and account access may be provided directly by the
          applicable regulated partner, fund, adviser, platform, administrator, or other approved third
          party.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default KYCVerificationPage;
